import { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { CONTRACT_ADDRESSES } from '@/contracts/config';
import { getChainConfig, getRpcUrlForChain } from '@/chains';
import { POD_PORTAL_FACTORY_ABI } from '@/contracts/pod';
import { isBackofficeOperator } from '@/config/backofficeOperators';

export type AccessStatus = 'loading' | 'admin' | 'operator' | 'denied' | 'disconnected';

export const useDashboardAccess = (walletAddress: string | null, chainId: number) => {
    const [status, setStatus] = useState<AccessStatus>('disconnected');

    useEffect(() => {
        if (!walletAddress) {
            setStatus('disconnected');
            return;
        }

        const check = async () => {
            setStatus('loading');

            try {
                // PoD chains (Sepolia/Fuji): factory-wide isAdmin / isOperator.
                const chainCfg = getChainConfig(chainId);
                if (chainCfg?.portalStrategy === 'pod-privacy-portal') {
                    // Allowlist is a full backoffice bypass (sees admin menu), matching
                    // on-chain factory admin for local/test wallets.
                    if (isBackofficeOperator(chainId, walletAddress)) {
                        setStatus('admin');
                        return;
                    }
                    const factoryAddress = chainCfg.addresses.PrivacyPortalFactory;
                    if (!factoryAddress) {
                        setStatus('denied');
                        return;
                    }
                    const provider = new ethers.JsonRpcProvider(getRpcUrlForChain(chainId));
                    const factory = new ethers.Contract(factoryAddress, POD_PORTAL_FACTORY_ABI, provider);
                    const [isAdmin, isOp] = await Promise.all([
                        factory.isAdmin(walletAddress).catch(() => false),
                        factory.isOperator(walletAddress).catch(() => false),
                    ]);
                    if (isAdmin) {
                        setStatus('admin');
                        return;
                    }
                    if (isOp) {
                        setStatus('operator');
                        return;
                    }
                    setStatus('denied');
                    return;
                }

                // Hardcoded per-chain allowlist — grants access when on-chain checks fail.
                if (isBackofficeOperator(chainId, walletAddress)) {
                    setStatus('operator');
                    return;
                }

                const addresses = CONTRACT_ADDRESSES[chainId as keyof typeof CONTRACT_ADDRESSES];
                if (!addresses) { setStatus('denied'); return; }

                // Use the native bridge as the authority contract — all bridges share the same deployer/operators
                const bridgeAddress = addresses.PrivacyBridgeCotiNative;
                if (!bridgeAddress) { setStatus('denied'); return; }

                const rpcUrl = chainId === 7082400
                    ? 'https://testnet.coti.io/rpc'
                    : 'https://mainnet.coti.io/rpc';
                const provider = new ethers.JsonRpcProvider(rpcUrl);

                const minAbi = [
                    "function isOperator(address account) view returns (bool)",
                    "function hasRole(bytes32 role, address account) view returns (bool)",
                    "function DEFAULT_ADMIN_ROLE() view returns (bytes32)",
                ];
                const contract = new ethers.Contract(bridgeAddress, minAbi, provider);

                const [adminRole, isOp] = await Promise.all([
                    contract.DEFAULT_ADMIN_ROLE(),
                    contract.isOperator(walletAddress),
                ]);
                const isAdmin = await contract.hasRole(adminRole, walletAddress);

                if (isAdmin) setStatus('admin');
                else if (isOp) setStatus('operator');
                else setStatus('denied');
            } catch (e) {
                console.error('useDashboardAccess check failed:', e);
                // PoD allowlist is a full bypass; COTI allowlist remains operator-level.
                const chainCfg = getChainConfig(chainId);
                if (isBackofficeOperator(chainId, walletAddress)) {
                    setStatus(chainCfg?.portalStrategy === 'pod-privacy-portal' ? 'admin' : 'operator');
                } else {
                    setStatus('denied');
                }
            }
        };

        check();
    }, [walletAddress, chainId]);

    return status;
};
